import { useShallow } from 'zustand/react/shallow';
import { useEffect } from 'react';
import { globals, logger } from 'common-ui';
import { useUserStore } from 'common-state';
import { selectUserDetails } from 'common-state/src/stores/userStore';
import aa from 'search-insights';

declare global {
  interface Window {
    OptanonWrapper?: () => void;
    OnetrustActiveGroups?: string;
    OneTrustGroupsUpdated?: () => void;
  }
}

interface AlgoliaConfig {
  appId: string;
  apiKey: string;
}

const ONE_TRUST_CONSENT_CATEGORY = '2';

const isServer = () => typeof window === 'undefined';

export const deleteCookie = (name: string): void => {
  if (isServer()) {
    return;
  }

  const safeName = encodeURIComponent(name);
  const isSecure = window.location.protocol === 'https:';
  document.cookie = `${safeName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${isSecure ? ' Secure;' : ''} SameSite=Lax`;
};

const getActiveConsentGroups = (): string[] => {
  const { OnetrustActiveGroups } = window;
  return OnetrustActiveGroups ? OnetrustActiveGroups.split(',').map(group => group.trim()) : [];
};

const initializeAlgoliaWithConsent = (hasConsent: boolean, brandAccountNumber?: string): void => {
  try {
    aa('init', { partial: true, useCookie: hasConsent });

    if (hasConsent) {
      logger.info('Algolia cookies enabled by consent');
      if (brandAccountNumber) {
        aa('setAuthenticatedUserToken', brandAccountNumber);
      }
    } else {
      logger.info('Algolia cookies disabled by consent');
      aa('setUserToken', '');
      deleteCookie('_ALGOLIA');
    }
  } catch (error) {
    logger.error('Failed to initialize Algolia with consent', error);
  }
};

export const handleConsent = (consentCategory: string, brandAccountNumber?: string): void => {
  if (isServer()) {
    return;
  }

  const activeGroups = getActiveConsentGroups();
  logger.info('Algolia OneTrust Active Groups', { activeGroups });

  const hasConsent = activeGroups.includes(consentCategory);
  initializeAlgoliaWithConsent(hasConsent, brandAccountNumber);
};

const validateAlgoliaConfig = (): AlgoliaConfig | null => {
  const { algoliaAppId, algoliaSearchKey } = globals ?? {};

  if (!algoliaAppId || !algoliaSearchKey) {
    logger.error('Algolia configuration missing', {
      algoliaAppId,
      algoliaSearchKey,
    });
    return null;
  }

  return { appId: algoliaAppId, apiKey: algoliaSearchKey };
};

const initializeAlgoliaBase = (config: AlgoliaConfig): boolean => {
  try {
    aa('init', config);
    logger.info('Algolia initialized with base config', config);
    return true;
  } catch (error) {
    logger.error('Failed to initialize Algolia', error);
    return false;
  }
};

const handleNonUKRegion = (): void => {
  try {
    aa('init', { partial: true, useCookie: true });
    logger.info('Algolia cookies enabled by default for non-UK region');
  } catch (error) {
    logger.error('Failed to enable Algolia cookies', error);
  }
};

export const useAlgoliaInit = (): void => {
  const userDetails = useUserStore(useShallow(selectUserDetails));
  const brandAccountNumber = userDetails?.brandAccountNumber;

  useEffect(() => {
    if (isServer()) {
      return;
    }

    const config = validateAlgoliaConfig();
    if (!config || !initializeAlgoliaBase(config)) {
      return;
    }

    const { country } = globals ?? {};

    if (country !== 'uk') {
      handleNonUKRegion();
      return;
    }

    const consentHandler = () => handleConsent(ONE_TRUST_CONSENT_CATEGORY, brandAccountNumber);

    window.addEventListener('OneTrustGroupsUpdated', consentHandler);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', consentHandler);
    };
  }, [brandAccountNumber]);
};
